import { applyPageMetadata } from "@/lib/page-metadata";
import { LastUpdated } from "@/components/site/LastUpdated";
import type { ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout, Section, Eyebrow, CTAButton, BOOKING_URL } from "@/components/site/Layout";
import { CaseStudy, serviceCaseStudy } from "@/components/site/CaseStudy";
import { TrustSignals } from "@/components/site/TrustSignals";
import { FAQSection } from "@/components/site/FAQSection";
import { HeroLeadForm } from "@/components/site/HeroLeadForm";
import PpcCostPerCaseCalculator from "@/components/blog/PpcCostPerCaseCalculator";
import { Check } from "lucide-react";
import { services } from "@/data/services";
import { jsonLdScript, ogImageFor } from "@/lib/seo";

const TITLE = "PPC Management for Law Firms | Attorney PPC Agency | PageOne Lawyers";
const DESCRIPTION =
  "PPC for law firms across Google Ads, Local Services Ads, Microsoft and Meta, with landing pages, call tracking and cost per signed case reporting.";
const URL = "https://pageonelawyers.com/services/ppc-for-law-firms";
// Preserve the existing share image from the previous URL.
const OG_IMAGE = ogImageFor("service", "ppc-advertising");

const FAQS = [
  {
    q: "How much does PPC cost for a law firm?",
    a: "It depends on your practice area, market and competition. You set the ad budget, which is paid directly to the platforms, and our management fee is quoted separately. Use the calculator above to see what a budget could return with your own numbers.",
  },
  {
    q: "Is PPC and AdWords for lawyers the same thing?",
    a: "Mostly. Google renamed AdWords to Google Ads in 2018. PPC also covers Local Services Ads, Microsoft Ads and paid social such as Facebook and Instagram.",
  },
  {
    q: "Should we use Local Services Ads or Google search ads?",
    a: "Usually both. Local Services Ads charge per lead and show the Google Screened badge, while search ads give you control over keywords, ad copy and landing pages. Running both lets you appear twice for the same search.",
  },
  {
    q: "Who owns the ad accounts?",
    a: "You do. Your Google Ads, Microsoft Ads and Meta accounts are set up in your firm's name, and you keep them and all the data if you ever leave.",
  },
  {
    q: "Do you build landing pages for lawyer PPC?",
    a: "Yes. We design and build dedicated landing pages for your campaigns, matched to the searches they serve and built to load fast on a phone.",
  },
  {
    q: "How do you know which ads bring in cases?",
    a: "We track calls, forms and chats from every campaign, then import signed cases from your intake software or CRM, so each campaign shows its true cost per signed case.",
  },
  {
    q: "Does Facebook PPC for lawyers work?",
    a: "It can, but differently from search. People on Facebook and Instagram aren't searching for a lawyer, so Meta ads work best for remarketing to past visitors and for awareness in practice areas such as estate planning and family law.",
  },
  {
    q: "How quickly does attorney PPC advertising bring in leads?",
    a: "Google reviews most ads within one business day, so ads can start running within days of approval. The first few weeks are spent cutting wasted searches and improving the pages, and results usually get better from there.",
  },
  {
    q: "Does lawyer PPC have to follow bar advertising rules?",
    a: "Yes. PPC advertising for attorneys is attorney advertising, so ads and landing pages must follow your state bar's rules, including the rule against false or misleading statements. We write within those rules, and nothing goes live without your approval.",
  },
];

const SCHEMAS = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${URL}#webpage`,
    url: URL,
    name: TITLE,
    description: DESCRIPTION,
    inLanguage: "en",
    isPartOf: { "@type": "WebSite", name: "PageOne Lawyers", url: "https://pageonelawyers.com/" },
    breadcrumb: { "@id": `${URL}#breadcrumb` },
    mainEntity: { "@id": `${URL}#service` },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${URL}#service`,
    name: "PPC Management for Law Firms and Attorneys",
    serviceType: "Pay-per-click advertising management",
    description:
      "PPC management for law firms and lawyers: Google Ads, Local Services Ads, Microsoft Ads and Meta campaigns, dedicated landing pages, call, form and signed case tracking, compliant ad copy and monthly reporting on cost per signed case.",
    url: URL,
    provider: { "@type": "Organization", name: "PageOne Lawyers", url: "https://pageonelawyers.com/" },
    audience: { "@type": "BusinessAudience", audienceType: "Law firms and attorneys" },
    areaServed: { "@type": "Country", name: "United States" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${URL}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://pageonelawyers.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://pageonelawyers.com/services" },
      { "@type": "ListItem", position: 3, name: "PPC for Law Firms", item: URL },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${URL}#faq`,
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

export const Route = createFileRoute("/services/ppc-for-law-firms")({
  head: () => applyPageMetadata("/services/ppc-for-law-firms", {
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:type", content: "image/svg+xml" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "PPC for Law Firms — PageOne Lawyers" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: SCHEMAS.map(jsonLdScript),
  }),
  component: PpcPage,
});

const H2 = ({ children }: { children: ReactNode }) => (
  <h2 className="max-w-4xl text-3xl font-semibold sm:text-4xl">{children}</h2>
);
const P = ({ children }: { children: ReactNode }) => (
  <p className="mt-4 max-w-4xl text-lg leading-relaxed text-muted-foreground">{children}</p>
);
const Item = ({ title, children }: { title: string; children: ReactNode }) => (
  <article className="rounded-lg border border-border bg-background p-6">
    <h3 className="text-xl font-semibold">{title}</h3>
    <P>{children}</P>
  </article>
);
const Fig = ({ src, alt, caption, height }: { src: string; alt: string; caption: string; height: number }) => (
  <figure className="mt-8 overflow-hidden rounded-lg border border-border bg-background">
    <img src={src} alt={alt} width="1200" height={height} loading="lazy" decoding="async" className="h-auto w-full" />
    <figcaption className="border-t border-border px-5 py-3 text-sm text-muted-foreground">{caption}</figcaption>
  </figure>
);
const Bullets = ({ items }: { items: string[] }) => (
  <ul className="mt-4 max-w-4xl list-disc space-y-2 pl-6 text-lg leading-relaxed text-muted-foreground">
    {items.map((i) => (
      <li key={i}>{i}</li>
    ))}
  </ul>
);
const PA = ({ slug, children }: { slug: string; children: ReactNode }) => (
  <Link to="/practice-areas/$slug" params={{ slug }} className="font-medium text-primary underline underline-offset-4 hover:text-gold-deep">
    {children}
  </Link>
);

function PpcPage() {
  return (
    <Layout>
      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-primary-foreground/70">
            <ol className="flex flex-wrap gap-1">
              <li>
                <Link to="/">Home</Link> /
              </li>
              <li>
                <Link to="/services">Services</Link> /
              </li>
              <li>PPC for Law Firms</li>
            </ol>
          </nav>
          <Eyebrow>
            <span className="text-gold">Service</span>
          </Eyebrow>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-semibold leading-tight sm:text-5xl md:text-6xl">
            {"PPC Management for Law Firms and Attorneys"}
          </h1><LastUpdated />
          <p className="mt-6 max-w-3xl text-xl text-primary-foreground/85">
            {"Google Ads, Local Services Ads, Microsoft Ads and Meta campaigns run by a legal PPC agency that measures signed cases, not clicks."}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get a free PPC account review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Talk to a strategist
            </CTAButton>
          </div>
        </div>
      </section>
      <Section>
        <P>
          {"PPC for law firms puts your firm in front of people at the moment they search for a lawyer, and you only pay when they click or call. That speed is also the risk: legal clicks are some of the most expensive in search advertising, so a loose campaign burns budget fast. As a law firm PPC agency, our attorney PPC management starts with tight targeting, landing pages built for each campaign and tracking that follows every lead through to a signed case. You own your ad accounts and see every dollar."}
        </P>
      </Section>

      <div className="border-y border-border bg-secondary/40">
        <Section>
          <H2>{"What's Included in Our PPC Services for Law Firms"}</H2>
          <P>{"Our law firm PPC services cover every platform where clients look for a lawyer, managed as one budget, so your money goes wherever it signs the most cases."}</P>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <Item title="Google Ads search campaigns">{"Campaigns built around the searches that bring in your best cases, split by practice area and location. Google PPC for lawyers lives or dies on search terms, so we review them every week and block the ones that waste money."}</Item>
            <Item title="Local Services Ads">{"Setup and management of Google's Local Services Ads, including the Google Screened verification process. You pay per lead rather than per click, and we dispute leads that don't qualify."}</Item>
            <Item title="Microsoft Ads">{"Your best Google campaigns, adapted for Bing and the Microsoft Advertising network, so you also reach the people who search there."}</Item>
            <Item title="Meta and display campaigns">{"Facebook and Instagram ads, YouTube and display remarketing to reach people who have already visited your site, and awareness campaigns in the practice areas where they make sense."}</Item>
            <Item title="Landing pages built for each campaign">{"We design and build dedicated landing pages for your campaigns, so the page matches the search, loads fast on a phone and makes calling you easy."}</Item>
            <Item title="Call, form and case tracking">{"Call tracking, form tracking and Google Analytics 4 set up properly, plus signed cases imported from your intake software or CRM, so every campaign is judged by the cases it produces."}</Item>
            <Item title="Search terms, negatives and bids">{"Weekly search term reviews, negative keyword lists, bid and budget changes, ad scheduling around your intake hours and location targeting that matches where you actually take cases."}</Item>
            <Item title="Compliant ad copy">{"Ads are attorney advertising. We write copy that follows your state bar's advertising rules, and nothing goes live without your approval."}</Item>
            <Item title="Monthly reporting">{"Spend, calls, forms, cost per lead and cost per signed case for every campaign, in plain language, with what we're changing next month."}</Item>
          </div>
        </Section>
      </div>

      <Section>
        <H2>{"How We Handle PPC Management for Law Firms"}</H2>
        <P>{"Good law firm PPC management is a weekly job, not a launch-and-leave setup. Our PPC management for lawyers follows the path from search to signed case and fixes the step where money leaks."}</P>
        <Fig
          src="/images/services/ppc-for-law-firms/ppc-search-to-signed-case.svg"
          alt="The path from a search to a signed case in law firm PPC: search, ad click, landing page, call or form, intake and signed case, with the common budget leak at each step."
          caption="Budget can leak at every step after the click, so we manage the whole path, not just the ads."
          height={460}
        />
        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <Item title="Audit or plan">{"If you've run ads before, we audit your accounts first: search terms, wasted spend, tracking and landing pages. If you're new to PPC, we plan budgets by practice area and market."}</Item>
          <Item title="Set up tracking first">{"Calls, forms and signed cases are tracked before a single dollar is spent, so attorney PPC management decisions are based on cases, not clicks."}</Item>
          <Item title="Build the campaigns">{"PPC campaigns for lawyers are split by practice area, case type and location, with ad copy written for each group of searches."}</Item>
          <Item title="Launch the landing pages">{"Each campaign points to a landing page built for it, never to your homepage."}</Item>
          <Item title="Optimise every week">{"Search terms, negatives, bids, budgets and ad tests are reviewed weekly. This is where lawyer PPC management earns its fee."}</Item>
          <Item title="Report and reallocate">{"Each month, budget moves toward the campaigns that sign cases and away from the ones that don't."}</Item>
        </div>
        <Fig
          src="/images/services/ppc-for-law-firms/where-legal-ads-appear.svg"
          alt="Mock search results page for a lawyer search with fictional firms, showing Local Services Ads at the top, then search ads, the map results and the organic results."
          caption="One search can show your firm in up to four places, so we plan paid and organic visibility together."
          height={800}
        />
      </Section>

      <div className="border-y border-border bg-secondary/40">
        <Section>
          <H2>{"Landing Pages Built for Lawyer PPC Advertising"}</H2>
          <P>{"Sending paid clicks to your homepage is the most common reason lawyer PPC advertising fails. The visitor searched for something specific, such as a truck accident lawyer in their city, and should land on a page about exactly that. The best landing page designs for lawyer PPC campaigns share the same parts: a headline that matches the search, a phone number that works with one tap, a short form, real proof of results and nothing that pulls the visitor away."}</P>
          <Fig
            src="/images/services/ppc-for-law-firms/lawyer-ppc-landing-page-anatomy.svg"
            alt="Annotated layout of a lawyer PPC landing page with a fictional firm: headline matching the search, tap-to-call button, short form, trust signals, practice-specific details and no main navigation."
            caption="Every part of the page has one job: turning a paid click into a call or form."
            height={760}
          />
        </Section>
      </div>

      <Section>
        <H2>{"Lawyer PPC Cost: What Drives Your Spend"}</H2>
        <P>{"There's no single price for PPC. Your lawyer PPC cost depends on your practice area, your market, how many firms are bidding against you and how well your ads and pages are built. Personal injury and mass tort searches in big cities sit at the expensive end; estate planning or immigration in a smaller market can cost far less per click. The ppc cost for law firms that matters most isn't the price of a click, though. It's the cost per signed case. The best ROI on PPC for law firms comes from fixing the steps after the click: landing pages, call handling and intake follow-up."}</P>
      </Section>

      <div className="border-y border-border bg-secondary/40">
        <Section>
          <H2>{"Work Out Your Cost per Signed Case"}</H2>
          <P>{"Enter your own numbers to see how many leads and signed cases your budget buys, what each case costs and which step would make the biggest difference."}</P>
          <PpcCostPerCaseCalculator />
        </Section>
      </div>

      <section id="free-plan" className="scroll-mt-24 bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-6 sm:py-20">
          <HeroLeadForm
            layout="split"
            headingLevel="h2"
            eyebrow={"FREE PPC ACCOUNT REVIEW"}
            heading={"Get a free review of your firm's ad accounts."}
            intro={"Send us your website and we'll come back within 24 hours with where your ad budget is leaking and what we'd change first. New to PPC? We'll tell you what a realistic starting budget looks like in your market."}
            sideContent={
              <>
                <p className="mt-7 text-sm font-bold">What we'll look at for your firm:</p>
                <ul className="mt-4 space-y-4 text-sm leading-relaxed text-primary-foreground/80">
                  {[
                    "Search terms you're paying for that will never become cases",
                    "Whether calls, forms and signed cases are tracked properly",
                    "How well your landing pages match your ads",
                    "Where your budget should move to sign more cases",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                      <span className="min-w-0">{t}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-primary-foreground/60">
                  Prefer to talk first?{" "}
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-4">
                    Book a strategy call
                  </a>
                </p>
              </>
            }
          />
        </div>
      </section>

      <Section>
        <H2>{"PPC Marketing for Law Firms Works Best Alongside SEO"}</H2>
        <P>{"Ads bring in cases from the first week; SEO builds the rankings that bring in cases without paying for every click. Run together, PPC marketing for lawyers shows you which searches turn into signed cases, and SEO then targets those searches for the long term. Firms that rely only on law firm PPC advertising pay for every lead forever. Firms that rely only on SEO wait months for results. As a law firm PPC company that also runs SEO, we plan your PPC marketing for attorneys alongside your rankings, so you're never paying for a click on a search you already rank first for without a good reason."}</P>
      </Section>

      <div className="border-y border-border bg-secondary/40">
        <Section>
          <H2>{"PPC for Attorneys in Every Practice Area"}</H2>
          <P>{"Click costs, search volumes and the way clients choose a lawyer are different in every practice area, so a PPC agency for attorneys has to build campaigns for each one."}</P>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <Item title="PPC for criminal defense lawyers">{"Criminal defense attorney PPC is urgent: people call within hours of an arrest. Criminal defense law firm PPC campaigns need call-focused ads, after-hours coverage and ad schedules that match when you can answer."}</Item>
            <Item title="DUI lawyer PPC">{"DUI attorney PPC works best with tight location targeting around courts and counties you cover, and ads that run late at night and on weekends."}</Item>
            <Item title="PPC for divorce lawyers and family law attorneys">{"Divorce attorney PPC and PPC for family law attorneys need careful targeting and a calm tone. Searches for custody, support and divorce are split into separate campaigns with their own landing pages. As a family law PPC company, we also exclude searches for free help and forms."}</Item>
            <Item title="Personal injury lawyer PPC">{"The most competitive clicks in legal advertising. Personal injury campaigns are split by accident type and backed by Local Services Ads so you're not paying top click prices for every lead."}</Item>
            <Item title="Immigration lawyer PPC">{"Immigration lawyer PPC often runs in more than one language, with separate campaigns and landing pages for each so the ad, the page and the person who answers the phone all match."}</Item>
            <Item title="PPC for employment lawyers">{"Employment law PPC targets employees with specific problems, such as wrongful termination or unpaid wages, and filters out employers and job seekers."}</Item>
            <Item title="PPC for estate & probate lawyers">{"Lower urgency, longer decisions. Search ads are paired with remarketing so families who are still deciding see your firm again."}</Item>
          </div>
          <P>
            Want to rank organically in these practice areas too? See our practice area SEO pages for{" "}
            <PA slug="criminal-defense-lawyer-seo">criminal defense lawyers</PA>,{" "}
            <PA slug="dui-dwi-lawyer-seo">DUI lawyers</PA>, <PA slug="family-law-lawyer-seo">family law</PA>,{" "}
            <PA slug="personal-injury-lawyer-seo">personal injury</PA>, <PA slug="immigration-lawyer-seo">immigration</PA>,{" "}
            <PA slug="employment-law-lawyer-seo">employment law</PA> and{" "}
            <PA slug="estate-planning-lawyer-seo">estate planning</PA>.
          </P>
        </Section>
      </div>

      <Section>
        <H2>{"PPC Services for Lawyers: How Pricing Works"}</H2>
        <P>{"Our PPC services for attorneys are priced separately from our SEO plans. Your quote depends on the platforms you use, the number of practice areas and markets and the size of your budget. Your ad spend is paid directly to Google, Microsoft or Meta from accounts you own, so you always see exactly where every dollar of your PPC advertising for law firms goes."}</P>
        <Bullets
          items={[
            "A management fee, quoted for your firm",
            "Ad spend paid directly to the ad platforms, from your accounts",
            "Landing pages and tracking set up as part of the work",
            "Monthly reporting on cost per lead and cost per signed case",
          ]}
        />
        <div className="mt-8">
          <CTAButton to="#free-plan" variant="gold">
            Get a PPC quote
          </CTAButton>
        </div>
      </Section>

      <div className="border-y border-border bg-secondary/40">
        <Section>
          <H2>{"Why Firms Choose PageOne as Their Law Firm PPC Agency"}</H2>
          <P>{"Most firms that come to us have worked with a generalist PPC agency for lawyers that reported on clicks and impressions. We only work with law firms, so as your lawyer PPC agency we track what matters to you: calls, consultations and signed cases. You own the accounts, you approve every ad, and you see the same numbers we do. That's what an attorney PPC agency should offer, and what most general agencies can't."}</P>
          <h3 className="mt-8 text-xl font-semibold">{"How to compare PPC agencies specializing in legal services"}</h3>
          <P>{"Whether you're choosing a PPC agency for law firms or a freelancer, ask the same questions:"}</P>
          <Bullets
            items={[
              "Will we own the ad accounts and all the data if we leave?",
              "Do you report on signed cases, or only on clicks and leads?",
              "Do you build dedicated landing pages for our campaigns?",
              "Can we see the search terms report and where our budget goes?",
              "Is your management fee separate from our ad spend?",
              "Do you work with law firms only, and do you understand bar advertising rules?",
            ]}
          />
          <P>{"A good legal PPC agency answers yes to every one. We do."}</P>
        </Section>
      </div>

      <CaseStudy {...serviceCaseStudy("PPC for Law Firms")} semanticSubheadings />
      <TrustSignals
        semanticSubheadings
        intro="Every ppc for law firms engagement is delivered by the same senior team, on the same guarantees, with reporting tied to signed cases — not clicks."
      />
      <FAQSection tone="muted" title={"Attorney PPC: Questions Firms Ask"} items={FAQS} semanticQuestionHeadings />

      <section className="bg-hero-gradient text-primary-foreground">
        <div className="mx-auto max-w-5xl px-5 py-14 text-center sm:px-6 sm:py-20">
          <h2 className="mx-auto max-w-4xl text-3xl font-semibold sm:text-4xl">{"Ready for Ads That Sign Cases?"}</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-primary-foreground/80">
            {"Get a free review of your ad accounts, or a realistic starting plan if you're new to PPC advertising for lawyers. A lawyer PPC company should show you its numbers, and we will."}
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap">
            <CTAButton to="#free-plan" variant="gold">
              Get my free PPC review
            </CTAButton>
            <CTAButton to={BOOKING_URL} variant="ghostLight">
              Book a strategy call
            </CTAButton>
          </div>
        </div>
      </section>

      <Section>
        <H2>{"Related Services"}</H2>
        <ul className="mt-6 flex flex-wrap gap-2">
          {services
            .filter((item) => item.slug !== "ppc-for-law-firms")
            .map((item) => (
              <li key={item.slug}>
                <Link
                  to="/services/$slug"
                  params={{ slug: item.slug }}
                  className="inline-block rounded-full border border-border px-4 py-2 text-sm transition hover:border-gold hover:text-gold-deep"
                >
                  {item.name}
                </Link>
              </li>
            ))}
        </ul>
      </Section>
    </Layout>
  );
}
